"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect } from "react";
// eslint-disable-next-line no-restricted-imports -- Suspense-wrapped in LayoutComponent
import { useSearchParams } from "next/navigation";
import { useEcomStore } from "@/context/ecom/EcomContext";
const OAUTH_CALLBACK_PARAMS = ["code", "state", "error", "error_description", "identity_provider"];

/**
 * Reactively strips OAuth callback params from the URL whenever Next's
 * router reports them as present - including right after Next reconciles
 * a Back/Forward navigation against its client-side router cache.
 *
 * We used to do this from a `popstate` listener with a `setTimeout` delay,
 * but Next's App Router has its own internal `popstate` handling that can
 * re-sync the address bar to its (stale, param-bearing) cached URL for
 * that history entry *after* our handler ran, undoing the cleanup - a
 * race whose outcome depended on unpredictable timing (visible as a
 * flicker that only "won" sometimes). Driving the cleanup off
 * `useSearchParams()` instead means our effect re-runs exactly when Next's
 * own router state actually changes, so we always run after Next has
 * settled rather than racing it.
 *
 * This intentionally only targets OAuth callback params (not UTM/marketing
 * params, which `useAuthCheck` restores from sessionStorage on its own).
 *
 * Gated on `isAuthCheckComplete`, same as useAuthCheck's own cleanup:
 * this component is rendered as a *child* of LayoutComponent, while
 * useAuthTID/useAuthCheck are hooks called *inside* LayoutComponent. React
 * fires child effects before the parent's own effects on mount, so without
 * this gate we'd strip `code`/`state` from the URL before useAuthTID's
 * mount effect ever gets a chance to read them - breaking the OAuth token
 * exchange on every fresh callback load, not just re-cleaning stale ones.
 */
export default function SsoParamsCleaner() {
  const $ = _c(6);
  const [state] = useEcomStore() ?? [];
  const searchParams = useSearchParams();
  let t0;
  if ($[0] !== searchParams || $[1] !== state?.isAuthCheckComplete) {
    t0 = () => {
      if (!state?.isAuthCheckComplete) {
        return;
      }
      const hasOAuthParams = OAUTH_CALLBACK_PARAMS.some(key => searchParams.has(key));
      if (!hasOAuthParams) {
        return;
      }
      const cleaned = new URLSearchParams(searchParams);
      for (const key_0 of OAUTH_CALLBACK_PARAMS) {
        cleaned.delete(key_0);
      }
      const newUrl = cleaned.size > 0 ? `${window.location.pathname}?${cleaned.toString()}` : window.location.pathname;
      window.history.replaceState(null, "", newUrl + window.location.hash);
    };
    $[0] = searchParams;
    $[1] = state?.isAuthCheckComplete;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const t1 = state?.isAuthCheckComplete;
  let t2;
  if ($[3] !== searchParams || $[4] !== t1) {
    t2 = [searchParams, t1];
    $[3] = searchParams;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  useEffect(t0, t2);
  return null;
}